import Autolinker from "autolinker";
import { SocialNetwork } from "./SocialNetwork";

/**
 * Turning a post's plain text into the HTML a card renders.
 *
 * Kept apart from apiProvider because it is the part with rules worth testing: Autolinker's
 * "twitter" mode rewrites #tags and @mentions into twitter.com URLs, which is right for a tweet
 * and silently wrong for anything else.
 */

const BLUESKY_PROFILE = "https://bsky.app/profile/";
const BLUESKY_TAG_SEARCH = "https://bsky.app/search?q=%23";
const INSTAGRAM = "https://www.instagram.com/";

/**
 * How a network whose text arrives plain links its @names and #tags. Each pattern captures what
 * precedes the sign, then the name without it; the boundary is spelled out rather than a lookbehind,
 * which a Safari before 16.4 cannot parse - and a regex it cannot parse stops the whole bundle.
 */
interface TextLinks {
    mention: RegExp;
    profile: (handle: string) => string;
    hashtag: RegExp;
    tag: (tag: string) => string;
}

const PLAIN_TEXT_LINKS: Partial<Record<SocialNetwork, TextLinks>> = {
    bluesky: {
        /** Bluesky handles are domains: @someone.bsky.social, @president.europarl.europa.eu. */
        mention: /(^|[\s(])@([a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)+)/gi,
        profile: handle => BLUESKY_PROFILE + handle,
        hashtag: /(^|[\s(])#([\p{L}\p{N}_]+)/gu,
        tag: tag => BLUESKY_TAG_SEARCH + tag,
    },
    instagram: {
        /** Letters, digits, _ and . up to 30, never ending on a full stop, which ends the sentence. */
        mention: /(^|[\s(])@([a-z0-9_](?:[a-z0-9_.]{0,28}[a-z0-9_])?)(?![a-z0-9_])/gi,
        profile: handle => `${INSTAGRAM}${handle}/`,
        /** As the API reads them for topics: at least one letter, so "#2026" is no tag (ops issues/018). */
        hashtag: /(^|[\s(])#([\p{L}\p{M}\p{N}_]*[\p{L}_][\p{L}\p{M}\p{N}_]*)/gu,
        tag: tag => `${INSTAGRAM}explore/tags/${tag}/`,
    },
};

/**
 * Plain text as HTML text. A Bluesky post or an Instagram caption is stored as its author typed it,
 * so "<b>" in it is three characters to show, not markup to build. X sends its text HTML-escaped
 * already, which is why tweets skip this: escaping them again would show "&amp;amp;".
 */
const escapeHtml = (text: string): string =>
    text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const link = (href: string, text: string): string =>
    `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;

/**
 * Links a post's text for the network it came from. Only Twitter gets Autolinker's twitter mode;
 * Bluesky and Instagram mentions and tags are linked to their own network, and URLs are linked
 * everywhere.
 */
export const linkifyContent = (content: string, source: SocialNetwork): string => {
    if (! content) {
        return "";
    }
    if (source === "twitter") {
        return Autolinker.link(content, { newWindow: true, hashtag: "twitter", mention: "twitter" });
    }

    const urlsLinked = Autolinker.link(escapeHtml(content), { newWindow: true, hashtag: false, mention: false });
    const links = PLAIN_TEXT_LINKS[source];
    if (! links) {
        return urlsLinked;
    }
    // Applied after Autolinker, so an @handle already inside an <a href> is left alone: the
    // patterns only match at a boundary that a generated tag does not produce.
    return urlsLinked
        .replace(links.mention, (match, before, handle) =>
            `${before}${link(links.profile(handle), "@" + handle)}`)
        .replace(links.hashtag, (match, before, tag) =>
            `${before}${link(links.tag(tag), "#" + tag)}`);
};

/** Where an account lives on its own network, for linking a reposted-from handle. */
export const profileUrl = (source: SocialNetwork, handle: string): string | undefined => {
    if (! handle) {
        return undefined;
    }
    if (source === "twitter") {
        return `https://x.com/${handle}`;
    }
    return PLAIN_TEXT_LINKS[source]?.profile(handle);
};

/** The shortened link X appends to a quote's text, pointing at the quoted tweet. */
const TRAILING_TCO = /\s*https?:\/\/t\.co\/\w+\s*$/;

/**
 * Drops the t.co link X puts at the end of a quote's text. It is the quoted tweet, and the card
 * now says who is quoted and links the post in its own line above the text, so the bare
 * "t.co/BA5DvASd3K" inside the text is the same link with a worse name. Only when the API named
 * the quoted post: without that, the link is the only way to it and must stay.
 */
export const stripQuoteLink = (content: string, postType?: string, quotedUrl?: string): string => {
    if (! content || postType !== "quote" || ! quotedUrl) {
        return content;
    }
    return content.replace(TRAILING_TCO, "");
};

/** "RT @handle: " at the very start, the shape the old ingest wrote into the content. */
const RT_PREFIX = /^RT @[A-Za-z0-9_.\-]+:\s*/;

/**
 * Drops the "RT @handle:" an older ingest put in front of a repost's text. The card names the
 * reposted account in its own header, so repeating it inside the text is noise - and it is display
 * text rather than data, which is why the API also sends repostedFrom.
 *
 * The ingest no longer writes it, but every repost already in the database still carries it, so
 * this stays. Twitter reposts stored before the ingest fix also have no repostedFrom, which is why
 * postType alone is enough to strip: without it those items would keep the prefix forever.
 */
export const stripRepostPrefix = (content: string, repostedFrom?: string,
                                  postType?: string): string => {
    if (! content) {
        return content;
    }
    if (repostedFrom) {
        const prefix = `RT @${repostedFrom}:`;
        if (content.startsWith(prefix)) {
            return content.slice(prefix.length).trimStart();
        }
    }
    return postType === "repost" ? content.replace(RT_PREFIX, "") : content;
};
